/**
 * @file Parent-board item mutation and closed-pagination helpers.
 */

import {
    collection as firestoreCollection,
    doc as firestoreDoc,
    getDocs as firestoreGetDocs,
    limit as firestoreLimit,
    orderBy as firestoreOrderBy,
    query as firestoreQuery,
    serverTimestamp as firestoreServerTimestamp,
    startAfter as firestoreStartAfter,
    updateDoc as firestoreUpdateDoc,
    where as firestoreWhere,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";

import { buildMyTaskPaginationQuery as configuredBuildMyTaskPaginationQuery } from "../../app/data/taskAccessQueries.js";
import {
    createStandardParentItemCallable as configuredCreateStandardParentItem,
    deleteItemCallable as configuredDeleteItemCallable,
} from "../../app/firebase/callables.js";
import { db as configuredDb } from "../../app/firebase/firebase.js";
import {
    appData as configuredAppData,
    state as configuredState,
} from "../../app/state/state.js";
import { renderCurrentBoard as configuredRenderCurrentBoard } from "../../app/view/viewController.js";
import { openConfirmationModal as configuredOpenConfirmationModal } from "../ui/confirmationModal.js";
import {
    findItem as configuredFindItem,
    getItemType as configuredGetItemType,
} from "../utils/itemLookup.js";
import {
    getMatchingChecklistTemplates as configuredGetMatchingChecklistTemplates,
} from "../details/checklistTemplates.js";
import {
    resolveItemClosedFromStatus as configuredResolveItemClosedFromStatus,
} from "../utils/closedState.js";
import {
    getBoardRegistryEntryByCollectionName,
} from "../board/boardRegistry.js";
import {
    sanitizeHTML as configuredSanitizeHTML,
} from "../utils/contentUtils.js";
import {
    NATIVE_CHECKBOX_CLASSES,
    NATIVE_CHECKBOX_ICON_CLASSES,
    NATIVE_CHECKBOX_LABEL_CLASSES,
    NATIVE_CHECKBOX_MARKER_CLASSES,
} from "../forms/nativeFieldStyles.js";

const PARENT_ITEM_TYPES = ['patient', 'project', 'asset', 'task', 'lab', 'maintenance'];
const CLOSED_ITEMS_LIMIT = 30;

const configuredCleanCurrency = (value) => {
    if ((value === null || value === undefined) ||
        typeof value !== 'string' ||
        value.trim() === '') {
        return null;
    }
    const number = Number(String(value).replace(/[$,]/g, ''));
    return Number.isNaN(number) || !Number.isFinite(number) ? null : number;
};

const configuredGetResolvedClosedUpdate = (itemType, statusId) => ({
    isClosedResolved: configuredResolveItemClosedFromStatus(itemType, statusId),
});

const getUpdatedAtValue = (item) => {
    const updatedAt = item?.updatedAt;
    if (!updatedAt) {return 0;}
    if (typeof updatedAt?.toMillis === 'function') {return updatedAt.toMillis();}
    if (typeof updatedAt?.seconds === 'number') {
        const nanos = typeof updatedAt?.nanoseconds === 'number' ?
            updatedAt.nanoseconds :
            0;
        return (updatedAt.seconds * 1000) + Math.floor(nanos / 1000000);
    }
    const parsed = new Date(updatedAt);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const mergeByUpdatedAt = (items = []) => {
    const merged = new Map();
    items.forEach((item) => {
        if (!item?.id) {return;}
        merged.set(item.id, item);
    });
    return Array.from(merged.values());
};

const isLinkedDependentBlockingResponse = (response = {}) =>
    response?.success === false && response?.reason === 'linked_dependents';

const renderLinkedDependentRows = (blockers = []) => {
    if (!Array.isArray(blockers) || blockers.length === 0) {
        return '<p class="text-sm text-gray-600 dark:text-gray-300">Linked items were found.</p>';
    }

    return `
        <ul class="mt-3 max-h-52 space-y-2 overflow-auto pr-1">
            ${blockers.map((item) => {
                const hasExplicitSubtitle =
                    Object.prototype.hasOwnProperty.call(item, "displaySubtitle");
                const subtitle = hasExplicitSubtitle ?
                    item.displaySubtitle :
                    (item.itemType || "linked item");
                const linkedTaskCount = Number(item.linkedTaskCount || 0);
                const linkedTaskLabel = linkedTaskCount > 0 ?
                    `+ ${linkedTaskCount} subtask${linkedTaskCount === 1 ? '' : 's'}` :
                    '';
                return `
                    <li class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900/40">
                        <div class="font-medium text-gray-900 dark:text-gray-100">${configuredSanitizeHTML(item.displayName || item.title || 'Untitled')}</div>
                        ${subtitle ? `<div class="mt-1 text-xs text-gray-500 dark:text-gray-400">${configuredSanitizeHTML(subtitle)}</div>` : ""}
                        ${linkedTaskLabel ? `<div class="mt-1 text-xs font-medium text-gray-600 dark:text-gray-300">${configuredSanitizeHTML(linkedTaskLabel)}</div>` : ""}
                    </li>
                `;
            }).join('')}
        </ul>
    `;
};

/**
 * Creates parent-board item API helpers.
 * @param {object} deps Dependency bag.
 * @returns {object} Parent item helpers.
 */
export function createParentItemsApi({
    createStandardParentItem,
    appData,
    buildMyTaskPaginationQuery,
    cleanCurrency,
    collection,
    db,
    doc,
    findItem,
    getDocs,
    getItemType,
    getMatchingChecklistTemplates,
    getResolvedClosedUpdate,
    limit,
    openConfirmationModal,
    orderBy,
    query,
    renderCurrentBoard,
    sanitizeHTML,
    serverTimestamp,
    startAfter,
    state,
    updateDoc,
    where,
}) {
    /**
     * Applies parent-board specific form payload shaping.
     * @param {object} params Form context.
     * @returns {boolean} Whether the type was handled as a parent item.
     */
    const applyParentItemFormData = ({
        data,
        formItemData,
        id,
        parentId,
        type,
    }) => {
        switch (type) {
            case 'patient':
                data.provider = Array.isArray(data.provider) ?
                    data.provider :
                    formItemData.provider || [];
                data.types = Array.isArray(data.types) ?
                    data.types :
                    formItemData.types || [];
                data.case_value = cleanCurrency(data.case_value);
                Object.assign(data, getResolvedClosedUpdate('patient', data.status));
                if (!id) {
                    data.createdBy = state.currentUser.uid;
                    data.createdAt = serverTimestamp();
                    data.updatedAt = serverTimestamp();
                }
                return true;
            case 'project':
                data.project_manager = Array.isArray(data.project_manager) ?
                    data.project_manager :
                    formItemData.project_manager || [];
                data.types = Array.isArray(data.types) ?
                    data.types :
                    formItemData.types || [];
                data.budget = cleanCurrency(data.budget);
                Object.assign(data, getResolvedClosedUpdate('project', data.status));
                if (!id) {
                    data.createdBy = state.currentUser.uid;
                    data.createdAt = serverTimestamp();
                    data.updatedAt = serverTimestamp();
                }
                return true;
            case 'asset':
                data.purchase_price = cleanCurrency(data.purchase_price);
                data.types = Array.isArray(data.types) ?
                    data.types :
                    formItemData.types || [];
                Object.assign(data, getResolvedClosedUpdate('asset', data.equipment_status));
                if (id) {
                    const existingItem = findItem(id);
                    if (!existingItem) {
                        throw new Error(`Asset with ID ${id} not found for editing.`);
                    }
                } else {
                    data.last_service_date = null;
                    data.next_service_due = null;
                    data.maintenance_issue_state = null;
                    data.notes = [];
                    data.issue_log = [];
                    data.service_log = [];
                    data.createdBy = state.currentUser.uid;
                    data.createdAt = serverTimestamp();
                    data.updatedAt = serverTimestamp();
                }
                return true;
            case 'task':
                data.assigned_users = Array.isArray(data.assigned_users) ?
                    data.assigned_users :
                    formItemData.assigned_users || [];
                data.types = Array.isArray(data.types) ?
                    data.types :
                    formItemData.types || [];
                Object.assign(data, getResolvedClosedUpdate('task', data.status));
                if (id && formItemData.parentId && !formItemData.parentType) {
                    const existingParentType = getItemType(formItemData.parentId);
                    if (existingParentType) {
                        data.parentType = existingParentType;
                    }
                }
                if (!id && parentId) {
                    data.parentId = parentId;
                    const parentType = getItemType(parentId);
                    if (parentType) {
                        data.parentType = parentType;
                    } else {
                        console.warn(
                            `applyParentItemFormData: Could not determine parentType for parentId ${parentId} when creating task.`,
                        );
                    }
                }
                data.isSubtask = Boolean(data.parentId || formItemData.parentId);
                if (!id) {
                    data.owner = data.owner || state.currentUser.uid;
                    data.createdBy = state.currentUser.uid;
                    data.createdAt = serverTimestamp();
                    data.updatedAt = serverTimestamp();
                }
                return true;
            default:
                return false;
        }
    };

    /**
     * Prompts for matching checklist templates on supported parent-board items.
     * @param {object} params Form context.
     * @returns {Promise<void>}
     */
    const maybeApplyChecklistTemplates = async ({ data, id, type }) => {
        if (!PARENT_ITEM_TYPES.includes(type) ||
            (id && data.checklist && Object.keys(data.checklist || {}).length > 0)) {
            return;
        }

        const itemTypes = data.types || [];
        const matchingTemplates = getMatchingChecklistTemplates(type, itemTypes, false);

        if (matchingTemplates.length === 0) {return;}

        try {
            const templateDataArray = await new Promise((resolve) => {
                let messageHtml;
                let heading;

                if (matchingTemplates.length > 1) {
                    heading = 'Checklist Templates Available';
                    const checkboxesHtml = matchingTemplates.map((template) => `
                        <label class="${NATIVE_CHECKBOX_LABEL_CLASSES} mb-2 items-start" data-native-checkbox-label>
                            <input type="checkbox" name="auto-apply-template" value="${sanitizeHTML(template.id)}" class="${NATIVE_CHECKBOX_CLASSES}" checked>
                            <span class="${NATIVE_CHECKBOX_MARKER_CLASSES} mt-0.5" data-native-checkbox-marker>
                                <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
                            </span>
                            <span class="text-sm text-gray-700 dark:text-gray-200">${sanitizeHTML(template.name)} checklist template</span>
                        </label>
                    `).join('');
                    messageHtml = `
                        <div class="mb-4 text-sm text-gray-600 dark:text-gray-400">Several checklist templates are available for this item type. Select any that you would like to apply.</div>
                        <div id="auto-checklist-template-checkboxes" class="mt-2 text-left">
                            ${checkboxesHtml}
                        </div>
                    `;
                } else {
                    heading = 'Checklist Template Available';
                    messageHtml = `<strong>${sanitizeHTML(matchingTemplates[0].name)}</strong> checklist template is available for this item type. Would you like to add it?`;
                }

                openConfirmationModal(
                    heading,
                    messageHtml,
                    () => {
                        if (matchingTemplates.length > 1) {
                            const checkboxes = document.querySelectorAll(
                                '#auto-checklist-template-checkboxes input[name="auto-apply-template"]:checked',
                            );
                            const selectedIds = Array.from(checkboxes)
                                .map((checkbox) => checkbox.value);
                            resolve(matchingTemplates.filter((template) =>
                                selectedIds.includes(template.id),
                            ));
                        } else {
                            resolve([matchingTemplates[0]]);
                        }
                    },
                    false,
                    'Apply',
                    () => resolve([]),
                    'Applying...',
                    "Don't Apply",
                );
            });

            if (!templateDataArray || templateDataArray.length === 0) {return;}

            data.checklist = {};
            let orderIndex = 0;
            templateDataArray.forEach((templateData) => {
                if (!templateData ||
                    !templateData.items ||
                    Object.keys(templateData.items).length === 0) {
                    return;
                }
                const itemsArray = Object.values(templateData.items)
                    .sort((a, b) => (a.order || 0) - (b.order || 0));
                itemsArray.forEach((item) => {
                    const newId = crypto.randomUUID();
                    data.checklist[newId] = {
                        text: item.text,
                        isCompleted: false,
                        order: orderIndex++,
                    };
                });
            });
        } catch (error) {
            console.error("Error in checklist template prompt:", error);
        }
    };

    /**
     * Deletes an item through the backend delete callable.
     * @param {string} itemId Item or log ID.
     * @param {string} itemType Item type.
     * @param {object | null} context Additional delete context.
     * @returns {Promise<void>}
     */
    const createDeleteItemHandler = (deleteItemCallable) => async (
        itemId,
        itemType,
        context = null,
    ) => {
        if (!itemId || !itemType) {
            console.error("handleDeleteItem: Missing itemId or itemType.");
            return;
        }
        if (itemType === 'user') {
            openConfirmationModal(
                'Restriction',
                'User deletion from the client app is currently disabled.',
                async () => { },
                false,
                'OK',
            );
            return;
        }

        const callDelete = async (linkedAction = null) => {
            const payload = {
                itemId,
                itemType,
                context: linkedAction ?
                    { ...(context || {}), linkedAction } :
                    context,
            };
            const result = await deleteItemCallable(payload);
            return result?.data || result;
        };

        const promptForLinkedDependentAction = (response) =>
            new Promise((resolve) => {
                const bodyHtml = `
                    <div class="text-sm text-gray-600 dark:text-gray-300">
                        <p>This ${sanitizeHTML(itemType)} has linked items. Choose whether to delete them or keep them without the parent link.</p>
                        ${renderLinkedDependentRows(response?.blockers || [])}
                    </div>
                `;
                const runAction = async (linkedAction) => {
                    try {
                        const finalResponse = await callDelete(linkedAction);
                        resolve(finalResponse);
                        return finalResponse;
                    } catch (error) {
                        resolve(false);
                        throw error;
                    }
                };

                openConfirmationModal(
                    'Delete Linked Items?',
                    bodyHtml,
                    () => runAction('delete_linked'),
                    true,
                    'Delete All',
                    () => resolve(false),
                    'Deleting...',
                    'Cancel',
                    {
                        leftAction: {
                            buttonText: 'Keep Items',
                            danger: false,
                            loadingText: 'Keeping...',
                            onClick: () => runAction('preserve_unlink'),
                            placement: 'beforeConfirm',
                            variant: 'secondary',
                        },
                    },
                );
            });

        try {
            const response = await callDelete();
            if (isLinkedDependentBlockingResponse(response)) {
                return await promptForLinkedDependentAction(response);
            }
            return response;
        } catch (error) {
            console.error(`Error calling deleteItemCallable for ${itemType} ${itemId}:`, error);
            const errorMessage = (error.code === 'internal' || !error.message)
                ? 'Something went wrong. Please try again later.'
                : `Failed to delete: ${error.message}`;
            openConfirmationModal('Error', errorMessage, async () => { }, false, 'OK');
            return false;
        }
    };

    const isValidClosedPaginationCollection = (collectionType) =>
        collectionType === "tasks" ||
        Boolean(getBoardRegistryEntryByCollectionName(collectionType)
            ?.capabilities?.supportsClosedPagination);

    /**
     * Loads another closed-item page into local board state.
     * @param {string} collectionType Collection key.
     * @returns {Promise<void>}
     */
    const loadMoreClosedItems = async (collectionType) => {
        if (!isValidClosedPaginationCollection(collectionType)) {
            console.error('loadMoreClosedItems: Invalid collection type:', collectionType);
            return;
        }

        const paginationState = state.closedItemsPagination[collectionType];

        try {
            if (collectionType === 'tasks') {
                if (!paginationState.hasMore) {
                    console.warn('loadMoreClosedItems: No more closed tasks to load.');
                    return;
                }

                const existingTaskIds = new Set(
                    (appData.tasks || []).map((item) => item.id),
                );
                const extraItemsById = new Map(
                    (paginationState.extraItems || []).map((item) => [item.id, item]),
                );

                const mergeClosedWindowIntoTasks = (visibleClosedItems) => {
                    const openTasks = (appData.tasks || [])
                        .filter((item) => item?.isClosedResolved !== true);
                    const mergedTasks = new Map(openTasks.map((item) => [item.id, item]));
                    visibleClosedItems.forEach((item) => {
                        if (item?.id) {mergedTasks.set(item.id, item);}
                    });
                    appData.tasks.splice(
                        0,
                        appData.tasks.length,
                        ...Array.from(mergedTasks.values()),
                    );
                };

                const getSortedClosedBuffer = () => mergeByUpdatedAt([
                    ...(paginationState.bufferItems || []),
                    ...extraItemsById.values(),
                ]).sort((a, b) => getUpdatedAtValue(b) - getUpdatedAtValue(a));

                const currentVisibleCount = Number.isFinite(paginationState.visibleCount) ?
                    paginationState.visibleCount :
                    CLOSED_ITEMS_LIMIT;
                paginationState.visibleCount = currentVisibleCount + CLOSED_ITEMS_LIMIT;

                if (!paginationState.taskQueryScopes ||
                    Object.keys(paginationState.taskQueryScopes).length === 0) {
                    paginationState.ownerHasMore = false;
                    paginationState.assignedHasMore = false;
                }

                let closedBuffer = getSortedClosedBuffer();
                let shouldFetchNextScopePage =
                    paginationState.ownerHasMore || paginationState.assignedHasMore;

                while (closedBuffer.length < paginationState.visibleCount ||
                    shouldFetchNextScopePage) {
                    const scopeEntries = Object.entries(
                        paginationState.taskQueryScopes || {},
                    ).filter(([, scopeState]) =>
                        scopeState?.hasMore && scopeState?.lastDoc,
                    );

                    if (scopeEntries.length === 0) {break;}

                    const scopedQueries = scopeEntries
                        .map(([key, scopeState]) => {
                            const spec = buildMyTaskPaginationQuery({
                                userId: state.currentUser.uid,
                                scopeState,
                                limitCount: CLOSED_ITEMS_LIMIT,
                            });
                            return spec ? { key, scopeState, spec } : null;
                        })
                        .filter(Boolean);

                    if (scopedQueries.length === 0) {break;}

                    const snapshots = await Promise.all(scopedQueries
                        .map((entry) => getDocs(entry.spec.queryRef)));

                    const fetchedItems = mergeByUpdatedAt(snapshots.flatMap(
                        (snapshot) => snapshot.docs.map((docSnapshot) => ({
                            id: docSnapshot.id,
                            ...docSnapshot.data(),
                        })),
                    ));

                    fetchedItems
                        .sort((a, b) => getUpdatedAtValue(b) - getUpdatedAtValue(a))
                        .forEach((item) => {
                            if (!existingTaskIds.has(item.id)) {
                                extraItemsById.set(item.id, item);
                            }
                        });

                    snapshots.forEach((snapshot, index) => {
                        const entry = scopedQueries[index];
                        entry.scopeState.hasMore =
                            snapshot.docs.length === CLOSED_ITEMS_LIMIT;
                        entry.scopeState.lastDoc = snapshot.docs.length > 0 ?
                            snapshot.docs[snapshot.docs.length - 1] :
                            entry.scopeState.lastDoc;
                        paginationState.taskQueryScopes[entry.key] =
                            entry.scopeState;
                    });

                    const scopeStates = Object.values(
                        paginationState.taskQueryScopes || {},
                    );
                    paginationState.ownerHasMore = scopeStates.some((scope) =>
                        scope.assignment === 'owner' && scope.hasMore);
                    paginationState.assignedHasMore = scopeStates.some((scope) =>
                        scope.assignment === 'assigned' && scope.hasMore);

                    if (snapshots.every((snapshot) => snapshot.empty)) {
                        break;
                    }

                    shouldFetchNextScopePage = false;
                    closedBuffer = getSortedClosedBuffer();
                }

                paginationState.extraItems = Array.from(extraItemsById.values())
                    .sort((a, b) => getUpdatedAtValue(b) - getUpdatedAtValue(a));
                paginationState.hasLoadedMore = true;
                closedBuffer = getSortedClosedBuffer();
                paginationState.bufferItems = closedBuffer;
                const visibleClosedItems = closedBuffer.slice(
                    0,
                    paginationState.visibleCount,
                );
                paginationState.bufferHasMore =
                    closedBuffer.length > visibleClosedItems.length;
                paginationState.hasMore = paginationState.bufferHasMore ||
                    paginationState.ownerHasMore ||
                    paginationState.assignedHasMore;
                mergeClosedWindowIntoTasks(visibleClosedItems);
                renderCurrentBoard();
                return;
            }

            if (!paginationState.hasMore || !paginationState.lastDoc) {
                console.warn(
                    `loadMoreClosedItems: No more items to load for ${collectionType}`,
                );
                return;
            }

            const nextQuery = query(
                collection(db, collectionType),
                where('isClosedResolved', '==', true),
                orderBy('updatedAt', 'desc'),
                startAfter(paginationState.lastDoc),
                limit(CLOSED_ITEMS_LIMIT),
            );

            const snapshot = await getDocs(nextQuery);
            const newItems = snapshot.docs
                .map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() }));

            if (newItems.length > 0) {
                const existingExtraIds = new Set(
                    (paginationState.extraItems || []).map((item) => item.id),
                );
                const uniqueNewItems = newItems
                    .filter((item) => !existingExtraIds.has(item.id));

                paginationState.extraItems = [
                    ...(paginationState.extraItems || []),
                    ...uniqueNewItems,
                ];
                paginationState.lastDoc = snapshot.docs[snapshot.docs.length - 1];
                paginationState.hasMore = snapshot.docs.length === CLOSED_ITEMS_LIMIT;
                paginationState.hasLoadedMore = true;

                const existingItems = appData[collectionType] || [];
                const existingIds = new Set(existingItems.map((item) => item.id));
                const itemsToAppend = uniqueNewItems
                    .filter((item) => !existingIds.has(item.id));
                appData[collectionType] = [...existingItems, ...itemsToAppend];
                renderCurrentBoard();
            } else {
                paginationState.hasMore = false;
            }
        } catch (error) {
            console.error(`Error loading more closed ${collectionType}:`, error);
            const errorMessage = (error.code === 'internal' || !error.message)
                ? 'Something went wrong. Please try again later.'
                : `Failed to load more items: ${error.message}`;
            openConfirmationModal('Error', errorMessage, async () => { }, false, 'OK');
        }
    };

    /**
     * Persists a standard parent-board item after form data is prepared.
     * @param {object} params Save context.
     * @returns {Promise<object|undefined>} Create result, or no update result.
     */
    const saveStandardParentItem = async ({
        collectionName,
        data,
        id,
    }) => {
        data.updatedBy = state.currentUser.uid;
        data.updatedAt = serverTimestamp();
        if (id) {
            const docRef = doc(db, collectionName, id);
            await updateDoc(docRef, data);
        } else {
            // Callable transport cannot serialize Firestore field transforms.
            const createData = { ...data };
            delete createData.createdAt;
            delete createData.updatedAt;
            const response = await createStandardParentItem({
                collectionName,
                data: createData,
            });
            return response.data;
        }
    };

    return {
        applyParentItemFormData,
        createDeleteItemHandler,
        loadMoreClosedItems,
        maybeApplyChecklistTemplates,
        saveStandardParentItem,
    };
}

const configuredParentItemsApi = createParentItemsApi({
    createStandardParentItem: configuredCreateStandardParentItem,
    appData: configuredAppData,
    buildMyTaskPaginationQuery: configuredBuildMyTaskPaginationQuery,
    cleanCurrency: configuredCleanCurrency,
    collection: firestoreCollection,
    db: configuredDb,
    doc: firestoreDoc,
    findItem: configuredFindItem,
    getDocs: firestoreGetDocs,
    getItemType: configuredGetItemType,
    getMatchingChecklistTemplates: configuredGetMatchingChecklistTemplates,
    getResolvedClosedUpdate: configuredGetResolvedClosedUpdate,
    limit: firestoreLimit,
    openConfirmationModal: configuredOpenConfirmationModal,
    orderBy: firestoreOrderBy,
    query: firestoreQuery,
    renderCurrentBoard: configuredRenderCurrentBoard,
    sanitizeHTML: configuredSanitizeHTML,
    serverTimestamp: firestoreServerTimestamp,
    startAfter: firestoreStartAfter,
    state: configuredState,
    updateDoc: firestoreUpdateDoc,
    where: firestoreWhere,
});

export const applyParentItemFormData =
    configuredParentItemsApi.applyParentItemFormData;
export const createDeleteItemHandler =
    configuredParentItemsApi.createDeleteItemHandler;
export const handleDeleteItem =
    configuredParentItemsApi.createDeleteItemHandler(configuredDeleteItemCallable);
export const loadMoreClosedItems =
    configuredParentItemsApi.loadMoreClosedItems;
export const maybeApplyChecklistTemplates =
    configuredParentItemsApi.maybeApplyChecklistTemplates;
export const saveStandardParentItem =
    configuredParentItemsApi.saveStandardParentItem;
